// src/services/patientSummary.js
// Shared by ChatPage.jsx (chat welcome bubble) and PatientTodayPage.jsx (the
// light-mode landing screen) — both need the same triage-history/care-plan/
// previu-age snapshot for their stat cards, so it's fetched in one place
// rather than drifting into two copies.

import { filterCarePlanItems } from '../utils/carePlanItems';

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

// ─── Parallel fetch of 3 summary endpoints ────────────────────────────────────
export async function fetchPatientSummary(patientId, token) {
  const headers = {
    Authorization:  `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const [triageRes, carePlanRes, ageRes] = await Promise.allSettled([
    fetch(`${API_BASE}/patients/${patientId}/triage-history`, { headers }),
    fetch(`${API_BASE}/patients/${patientId}/care-plan`,      { headers }),
    fetch(`${API_BASE}/previu-age/${patientId}`,              { headers }),
    // NOTE: no double /api/ on previu-age (CONTEXT.md Sprint 10)
  ]);

  // ── Triage history ──────────────────────────────────────────────────────
  // Field: result.consensusRiskLevel (camelCase, confirmed PatientTriageResultPage.jsx)
  // Field: result.drawDate (confirmed PatientTriageResultPage.jsx)
  let lastResult = null; // the most recent triage result object
  let triageHistoryCount = 0;
  if (triageRes.status === 'fulfilled' && triageRes.value.ok) {
    const history = await triageRes.value.json();
    if (Array.isArray(history) && history.length > 0) {
      lastResult = history[0]; // already sorted newest-first
      triageHistoryCount = history.length;
    }
  }

  // ── Care plan ───────────────────────────────────────────────────────────
  // Field: item.completed (boolean, confirmed PatientCarePlanPage.jsx)
  // filterCarePlanItems strips [TEST]-tagged items and blank doctor notes —
  // the same filter PatientCarePlanPage.jsx applies before computing its own
  // Open/Done/Total stats — so openActionItems here always matches what the
  // patient actually sees as "Open" on /patient/care-plan, including via
  // the Today screen's attention banner, which reads this field directly.
  let openActionItems  = 0;
  let totalActionItems = 0;
  let openItems        = []; // the open ones themselves, so Today can name them
  if (carePlanRes.status === 'fulfilled' && carePlanRes.value.ok) {
    const items = filterCarePlanItems(await carePlanRes.value.json());
    totalActionItems = items.length;
    openItems        = items.filter(i => !i.completed);
    openActionItems  = openItems.length;
  }

  // ── Previu Age ──────────────────────────────────────────────────────────
  // Field: data.previuAgeYears  (confirmed PatientPreviuAgePage.jsx line 54)
  // Field: data.ageDeltaYears   (confirmed PatientPreviuAgePage.jsx line 56)
  // 204 = fewer than 2 draws — leave null
  let previuAge      = null;
  let previuAgeDelta = null;
  if (ageRes.status === 'fulfilled' && ageRes.value.ok && ageRes.value.status !== 204) {
    try {
      const data = await ageRes.value.json();
      previuAge      = data.previuAgeYears != null
        ? parseFloat(data.previuAgeYears).toFixed(1) : null;
      previuAgeDelta = data.ageDeltaYears  != null
        ? parseFloat(data.ageDeltaYears).toFixed(1)  : null;
    } catch {
      // 204 No Content resolves as ok but body parse fails — safe to ignore
    }
  }

  return { lastResult, openActionItems, openItems, totalActionItems, previuAge, previuAgeDelta, triageHistoryCount };
}
